import React, { useState, useEffect } from 'react';
import { 
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import axios from 'axios';
import { message } from 'antd';

const timeRangeOptions = [
  { value: '1h', label: 'Last 1 Hour', seconds: 3600 },
  { value: '3h', label: 'Last 3 Hours', seconds: 10800 },
  { value: '6h', label: 'Last 6 Hours', seconds: 21600 },
  { value: '12h', label: 'Last 12 Hours', seconds: 43200 },
  { value: '24h', label: 'Last 24 Hours', seconds: 86400 },
  { value: '7d', label: 'Last 7 Days', seconds: 604800 },
];

const metricConfigs = [
  { 
    id: 'CPUUtilization',
    name: 'CPU Utilization',
    unit: '%',
    color: '#6366f1',
    description: 'The percentage of CPU utilization'
  },
  { 
    id: 'DatabaseConnections',
    name: 'Database Connections',
    unit: '',
    color: '#10b981',
    description: 'The number of database connections in use'
  },
  { 
    id: 'FreeableMemory',
    name: 'Freeable Memory',
    unit: 'MB',
    color: '#f59e0b',
    description: 'The amount of available random access memory',
    valueFormatter: (value) => (value / (1024 * 1024)).toFixed(2) // Convert bytes to MB
  },
  { 
    id: 'FreeStorageSpace',
    name: 'Free Storage Space',
    unit: 'GB',
    color: '#3b82f6',
    description: 'The amount of available storage space',
    valueFormatter: (value) => (value / (1024 * 1024 * 1024)).toFixed(2) // Convert bytes to GB
  },
  { 
    id: 'ReadIOPS',
    name: 'Read IOPS',
    unit: 'count/second',
    color: '#8b5cf6',
    description: 'The average number of disk read I/O operations per second'
  },
  { 
    id: 'WriteIOPS',
    name: 'Write IOPS',
    unit: 'count/second',
    color: '#ec4899',
    description: 'The average number of disk write I/O operations per second'
  },
  { 
    id: 'ReadLatency',
    name: 'Read Latency',
    unit: 'ms',
    color: '#0ea5e9',
    description: 'The average amount of time taken per disk I/O read operation'
  },
  { 
    id: 'WriteLatency',
    name: 'Write Latency',
    unit: 'ms',
    color: '#14b8a6',
    description: 'The average amount of time taken per disk I/O write operation'
  },
  { 
    id: 'ReadThroughput',
    name: 'Read Throughput',
    unit: 'MB/s',
    color: '#d946ef',
    description: 'The average number of bytes read from disk per second',
    valueFormatter: (value) => (value / (1024 * 1024)).toFixed(2) // Convert bytes to MB
  },
  { 
    id: 'WriteThroughput',
    name: 'Write Throughput',
    unit: 'MB/s',
    color: '#f97316',
    description: 'The average number of bytes written to disk per second',
    valueFormatter: (value) => (value / (1024 * 1024)).toFixed(2) // Convert bytes to MB
  }
];

const Metrics = ({ databaseInfo }) => {
  const [timeRange, setTimeRange] = useState('1h');
  const [metricsData, setMetricsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const databaseIdentifier = databaseInfo?.databaseName;
  
  const fetchMetrics = async () => {
    try {
      setError(null);
      setLoading(true);

      const selectedRange = timeRangeOptions.find(option => option.value === timeRange);
      const params = new URLSearchParams({
        timeRange: selectedRange.seconds.toString()
      });

      const response = await axios.get(`https://cloudify-prod.wizr.in/rds/instances/${databaseIdentifier}/metrics?${params.toString()}`);

      if (response.status !== 200) {
        throw new Error('Failed to fetch metrics');
      }

      setMetricsData(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setError('Failed to load metrics. Please try again.');
      message.error('Failed to load database metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (databaseIdentifier) {
      fetchMetrics();
    }
  }, [databaseIdentifier, timeRange]);

  const formatXAxis = (timestamp) => {
    const date = new Date(timestamp);
    if (timeRange === '7d') {
      return `${date.getMonth()+1}/${date.getDate()}`;
    }
    // Use 24-hour format (HH:MM)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatValue = (value, metric) => {
    if (metric.valueFormatter) {
      return metric.valueFormatter(value);
    }
    return value.toFixed(2);
  };

  const CustomTooltip = ({ active, payload, label, metric }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-2">{formatXAxis(label)}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <p 
                key={index} 
                className="text-sm flex items-center justify-between gap-4"
              >
                <span style={{ color: entry.color }}>{entry.name}:</span>
                <span className="font-medium">
                  {formatValue(entry.value, metric)} {metric.unit}
                </span>
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading && !Object.keys(metricsData).length) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error && !Object.keys(metricsData).length) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600 text-sm">{error}</p>
        <button
          onClick={fetchMetrics}
          className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const prepareChartData = (metricId) => {
    if (!metricsData[metricId]) return [];
    
    return metricsData[metricId].map(point => ({
      timestamp: point.timestamp,
      value: point.value
    }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Database Metrics</h2>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="block w-40 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-colors"
            >
              {timeRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-6">
            {metricConfigs.map((metric) => (
              <div key={metric.id} className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{metric.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">{metric.description}</p>
                  </div>
                  <div className="text-xs text-gray-500">{metric.unit}</div>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={prepareChartData(metric.id)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="timestamp" 
                        tickFormatter={formatXAxis}
                        stroke="#6b7280"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <YAxis 
                        stroke="#6b7280"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        tickFormatter={(value) => formatValue(value, metric)}
                      />
                      <Tooltip content={<CustomTooltip metric={metric} />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={metric.color}
                        strokeWidth={2}
                        name={metric.name}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Metrics;